import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Search, Loader2, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { DropdownResults } from '@/components/ui/dropdown-results'
import { MobileSlideInMenu } from '@/components/layout/MobileSlideInMenu'
import { useSearch } from '@/contexts/SearchContext'
import { useDropdownNavigation } from '@/hooks/useDropdownNavigation'
import { SearchResult } from '@/types/search'
import { cn } from '@/lib/utils/cn'

interface SearchBarProps {
  className?: string
  placeholder?: string
  showViewAllButton?: boolean
}

export function SearchBar({
  className,
  placeholder = 'Search users, projects, tasks...',
  showViewAllButton = true,
}: SearchBarProps) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [isMobileExpanded, setIsMobileExpanded] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const location = useLocation()

  const { results, loading, getSuggestions, clearSearch } = useSearch()

  // Use the consolidated navigation hook
  const {
    selectedIndex,
    resultItemRefs,
    viewAllButtonRef,
    handleKeyDown,
    handleResultClickForDropdown,
    handleViewAllClick,
  } = useDropdownNavigation({
    isOpen,
    results,
    hasViewAllButton: showViewAllButton,
    shouldShowViewAllButton: showViewAllButton && Boolean(query.trim()),
    onDropdownOpenChange: setIsOpen,
    onResultClick: (result: SearchResult) => {
      clearSearch()
      navigate(result.url)
    },
    onViewAllClick: () => {
      if (query.trim()) {
        clearSearch()
        navigate(`/search?q=${encodeURIComponent(query)}`)
      }
    },
  })

  // Clear search input when navigation occurs
  useEffect(() => {
    setQuery('')
    setIsOpen(false)
    setIsMobileExpanded(false)
    resultItemRefs.current = []
  }, [location.pathname, resultItemRefs])

  // Debounced search suggestions
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (query.trim()) {
        await getSuggestions(query)
        setIsOpen(true)
      } else {
        setIsOpen(false)
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [query, getSuggestions])

  const handleInputFocus = useCallback(() => {
    if (query.trim() && results.length > 0) {
      setIsOpen(true)
    }
  }, [query, results])

  const handleInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // Check if focus is moving to an element within the dropdown
      const relatedTarget = e.relatedTarget as HTMLElement
      if (relatedTarget && dropdownRef.current?.contains(relatedTarget)) {
        // Focus is moving to dropdown, keep it open
        return
      }

      // Delay hiding to allow for dropdown clicks
      setTimeout(() => {
        setIsOpen(false)
        setIsMobileExpanded(false)
        // Clear the refs array
        resultItemRefs.current = []
      }, 200)
    },
    [resultItemRefs]
  )

  const handleMobileSearchClick = useCallback(() => {
    setIsMobileExpanded((prev) => !prev)
    // Focus input after expansion with retry logic for mobile reliability
    const focusInput = () => {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }

    // Try to focus immediately first
    focusInput()

    // Also try after a short delay to handle mobile rendering variations
    setTimeout(focusInput, 50)
    setTimeout(focusInput, 150)
  }, [])

  const handleClearSearch = useCallback(() => {
    setQuery('')
    clearSearch()
    setIsOpen(false)
    setIsMobileExpanded(false)
    resultItemRefs.current = []
    inputRef.current?.focus()
  }, [clearSearch, resultItemRefs])

  return (
    <div className={cn('relative w-full max-w-lg', className)}>
      {/* Mobile: Show icon button when search menu is closed */}
      <Button
        variant="ghost"
        size="icon"
        onClick={handleMobileSearchClick}
        className="md:hidden"
        aria-label="Search"
      >
        <Search className="h-5 w-5" />
      </Button>

      {/* Desktop: Always show search input */}
      <div className="relative hidden md:block">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          className="pl-10 pr-10"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
        {query && !loading && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClearSearch}
            className="absolute right-3 top-1/2 h-6 w-6 -translate-y-1/2 hover:bg-muted"
            aria-label="Clear search"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {/* Mobile Slide-in Menu */}
      <MobileSlideInMenu
        isOpen={isMobileExpanded}
        onClose={() => {
          setIsMobileExpanded(false)
          handleClearSearch()
        }}
        mode="top"
        id="mobile-search"
        heading="Search"
      >
        <div className="space-y-4 pb-4">
          {/* Mobile Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={inputRef}
              type="text"
              placeholder={placeholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="pl-10 pr-10"
              autoFocus
            />
            {loading && (
              <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
            )}
            {query && !loading && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 h-6 w-6 -translate-y-1/2 hover:bg-muted"
                aria-label="Clear search"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>

          {/* Mobile Dropdown Results */}
          {isOpen && (query.trim() || results.length > 0) && (
            <div className="space-y-2">
              <DropdownResults
                label="Search Results"
                results={results}
                query={query}
                loading={loading}
                selectedIndex={selectedIndex}
                showViewAllButton={showViewAllButton}
                onResultClick={handleResultClickForDropdown}
                onViewAllClick={handleViewAllClick}
                resultItemRefs={resultItemRefs}
                viewAllButtonRef={viewAllButtonRef}
                mobile={true}
              />
            </div>
          )}
        </div>
      </MobileSlideInMenu>

      {/* Desktop Dropdown - only show on desktop */}
      {isOpen && (query.trim() || results.length > 0) && !isMobileExpanded && (
        <div
          ref={dropdownRef}
          onBlur={(e) => {
            // Check if focus is moving outside the dropdown and input
            const relatedTarget = e.relatedTarget as HTMLElement
            if (
              relatedTarget &&
              !dropdownRef.current?.contains(relatedTarget) &&
              relatedTarget !== inputRef.current
            ) {
              setTimeout(() => {
                setIsOpen(false)
                setIsMobileExpanded(false)
                resultItemRefs.current = []
              }, 100)
            }
          }}
        >
          <DropdownResults
            label="Search Results"
            results={results}
            query={query}
            loading={loading}
            selectedIndex={selectedIndex}
            showViewAllButton={showViewAllButton}
            onResultClick={handleResultClickForDropdown}
            onViewAllClick={handleViewAllClick}
            resultItemRefs={resultItemRefs}
            viewAllButtonRef={viewAllButtonRef}
          />
        </div>
      )}
    </div>
  )
}
